import { Button, CustomClaimValue, DetectionZone, Device, Devices, Dialog, Facilities, Facility, FacilityLayoutConfiguration, LaneLine, MINIMUM_ZONE_VERTICES, ScreenLine, Spot, Vector2, Icons } from "@app/shared";
import cn from "classnames";
import React, { forwardRef, MouseEventHandler, PropsWithChildren, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { dismissToast, toastError, toastInfo, toastSuccess, toastWarning, useFacilityCoordsHelper, toggleOverzoom } from "../../../helpers";
import { useFacilityCenter } from "../../../helpers/mapHelpers";
import { useConfirmDialog, useHeldKeyboardKeys } from "../../../hooks";
import { useEditModelDialog } from "../../../hooks/useEditModelDialog";
import useRenderingTrace from "../../../hooks/useRenderingTrace";
import { AuthorizeView } from "../AuthorizeView";
import { MaxHeightContainer } from "../charts/MaxHeightContainer";
import { InputRow, NumericInput } from "../inputs";
import { LoadingIndicator } from "../loading/LoadingIndicator";
import { MapShape } from "../maps/MapShape";
import { MapCompass } from "../maps/MapCompass";
import { GroupMapShape } from "../maps/MapShapes/GroupMapShape";
import { ParkingSpotGroupMapShape } from "../maps/MapShapes/ParkingSpotGroupMapShape";
import { RectangleMapShape } from "../maps/MapShapes/RectangleMapShape";
import { AlignOriginHandle } from "../maps/MapShapes/AlignOriginHandle";
import { OmniGoogleMap, OmniMapMethods } from "../maps/OmniGoogleMap";
import { MapGrid } from "../maps/MapGrid";
import { MapMeasurements } from "../maps/MapMeasurements";
import { EditToolPurpose, EditToolType, EditTools } from "./EditTool";
import { DeviceReferenceAlignmentDialog, ReferenceAlignmentResult } from "./DeviceReferenceAlignmentDialog";
import { GenerateLaneLinesDialog } from "./GenerateLaneLinesDialog";
import { CollectAndGenerateDialog } from "./CollectAndGenerateDialog";
import { hasLaneCollection } from "../../../helpers/versionCompare";
import { ShapeConfiguration, shapeToName } from "./ShapeConfiguration";
import { DeviceCameraView } from "./DeviceCameraView";
import { DeviceSnapshotState } from "../../../hooks/useDeviceSnapshot";
import { ShapeFilter } from "./ShapeFilter";
import { useShapeManager, useToolManager, useMapManager, useShapeLifecycle, useDeviceAlignment, EditMode } from "./hooks";
import { DeviceAlignmentLayer } from "./DeviceAlignmentLayer";
import { GridSettings } from "../../../helpers/snapToGrid";
import "./LayoutDesigner.css";

const NAV_SIDEBAR_WIDTH = 85; // px — must match the fixed sidebar nav width

// The facility origin spot arrives from the server without an id (Spot.Id is a nullable Guid the
// geometry converter never sets). The shape manager tracks local edits by id, so give the origin a
// stable stand-in id or align-mode Reset can't remove the edited spot (it would silently stay and
// "Done" snapped back to the dragged position). Must stay a valid GUID — the server parses it.
const FACILITY_ORIGIN_SPOT_ID = "00000000-0000-4000-8000-000000000001";

type LayoutDesignerProps = {
    facility: Facility;
    onChange?: () => void;
    onSaveLayoutConfiguration?: (layoutConfiguration: FacilityLayoutConfiguration) => Promise<void> | void;
    persistLayoutConfiguration?: boolean;
    allowEdit?: boolean;
    className?: string;
    hideWarning?: boolean;
    shapeFilter?: ShapeFilter;
    showMapOnly?: boolean;
    defaultShowLabels?: {
        detectionZones?: boolean;
        laneLines?: boolean;
        screenLines?: boolean;
    };
    mapMargins?: { left: number; right: number; top: number; bottom: number };
    mapHeight?: number;
    defaultDeviceId?: string;
    /** Select this device and open reference-point alignment as soon as it is available. */
    autoAlignDeviceId?: string;
    /** Fired once autoAlignDeviceId has actually been acted on, so the caller can drop it. */
    onAutoAlignOpened?: () => void;
    hideLaneGenerationScope?: boolean;
    /** Host-supplied camera snapshot, for embedders whose API isn't CoreApi (see DeviceCameraView). */
    cameraSnapshotOverride?: DeviceSnapshotState;
};

export interface LayoutDesignerMethods {
    setEditMode: (editMode: EditMode) => void;
    centerMap: () => void;
    fitDevicesOnMap: () => void;
    focusShapeById: (shapeId: string) => boolean;
    setMapZoom: (zoom: number) => void;
    getLayoutConfiguration: () => FacilityLayoutConfiguration;
    openCollectAndGenerate: () => void;
    /**
     * Select a device and open reference-point alignment on it. Used by the provisioning flow to
     * hand a freshly added device straight into calibration, since that is the only place pose can
     * be set without stranding camera-drawn shapes on coordinates derived from the old pose.
     */
    openDeviceAlignment: (deviceId: string) => void;
}

const DEFAULT_SHAPE_FILTER = ShapeFilter.showAll();

export const LayoutDesigner = forwardRef<LayoutDesignerMethods, PropsWithChildren<LayoutDesignerProps>>((props, ref) => {
    const { mapHeight, mapMargins, showMapOnly = false, facility, onChange, onSaveLayoutConfiguration, persistLayoutConfiguration = true, allowEdit = true, shapeFilter = DEFAULT_SHAPE_FILTER, defaultShowLabels, defaultDeviceId, autoAlignDeviceId, onAutoAlignOpened, hideLaneGenerationScope, cameraSnapshotOverride } = props;
    const shapeManager = useShapeManager({ facility, shapeFilter });
    const toolManager = useToolManager({ facility, shapeManager, defaultDeviceId });
    const mapRef = useRef<OmniMapMethods>(null);

    const [alignMode, setAlignMode] = useState(false);
    const [draftOrigin, setDraftOrigin] = useState<Vector2 | null>(null);
    const alignBaselineRef = useRef<Vector2 | null>(null);
    const alignHadPriorEditRef = useRef(false);

    const makeOriginSpot = useCallback(
        (o: Vector2) => {
            const v = new Vector2({ x: o.x, y: o.y });
            const spot = new Spot();
            spot.id = facility.originReferencePointLatLng?.id ?? FACILITY_ORIGIN_SPOT_ID;
            spot.name = facility.originReferencePointLatLng?.name;
            spot.points = [v];
            spot.origin = v;
            return spot;
        },
        [facility.originReferencePointLatLng]
    );

    const editedOriginSpot = useMemo(() => shapeManager.localShapes.find((s) => s.$type === Spot.$type) as Spot | undefined, [shapeManager.localShapes]);

    const liveFacility = useMemo(() => {
        const withOrigin = (spot: Spot) => Object.assign(Object.create(Object.getPrototypeOf(facility)), facility, { originReferencePointLatLng: spot });
        if (alignMode && draftOrigin) return withOrigin(makeOriginSpot(draftOrigin));
        if (editedOriginSpot) return withOrigin(editedOriginSpot);
        return facility;
    }, [facility, alignMode, draftOrigin, editedOriginSpot, makeOriginSpot]);

    const baseCoordsHelper = useFacilityCoordsHelper(facility);
    const coordsHelper = useFacilityCoordsHelper(liveFacility);
    const mapManager = useMapManager({ mapRef, coordsHelper, shapeManager, toolManager });
    const shapeLifecycle = useShapeLifecycle({
        facilityId: facility.id,
        shapeManager,
        toolManager,
    });
    const deviceAlignment = useDeviceAlignment({ facility, shapeManager });

    const [isSaving, setIsSaving] = useState(false);
    const [facilityCenter, isLoading] = useFacilityCenter(facility);
    const [mapControlsReady, setMapControlsReady] = useState(false);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
    const [isMobileControlsOpen, setIsMobileControlsOpen] = useState(false);

    // Top-down map vs device-camera perspective view
    // Saving refetches the facility and remounts this component, which would otherwise drop the
    // user back to the map every time. Persist the choice per facility so they stay where they were.
    const viewModeStorageKey = `layoutDesigner:viewMode:${facility?.id ?? "default"}`;
    const [viewMode, setViewMode] = useState<"map" | "camera">(() => {
        try {
            return localStorage.getItem(viewModeStorageKey) === "camera" ? "camera" : "map";
        } catch {
            return "map";
        }
    });
    const [cameraDeviceId, setCameraDeviceId] = useState<string | undefined>(defaultDeviceId ?? facility.devices?.[0]?.id);
    const [showAlignTracks, setShowAlignTracks] = useState(true);

    // "Align Device" flow: choice prompt, then either manual drag-align or the reference-point fit.
    const [alignChoiceOpen, setAlignChoiceOpen] = useState(false);
    const [referenceAlignOpen, setReferenceAlignOpen] = useState(false);

    // Label visibility state
    const [showDetectionZoneLabels, setShowDetectionZoneLabels] = useState(defaultShowLabels?.detectionZones ?? true);
    const [showLaneLineLabels, setShowLaneLineLabels] = useState(defaultShowLabels?.laneLines ?? false);
    const [showScreenLineLabels, setShowScreenLineLabels] = useState(defaultShowLabels?.screenLines ?? false);

    // Grid and snap settings
    const [gridSettings, setGridSettings] = useState<GridSettings>({
        enabled: false,
        sizeMeters: 1.0, // Default 1 meter grid
    });

    // Measurement display settings
    const [measurementsEnabled, setMeasurementsEnabled] = useState(false);

    // Super Zoom, going above 22 levels
    const [superZoomEnabled] = useState(true);

    // Generate-lane-lines-from-traffic dialog
    const [isGenerateLaneLinesOpen, setIsGenerateLaneLinesOpen] = useState(false);
    const [isCollectOpen, setIsCollectOpen] = useState(false);
    const [reviewCtx, setReviewCtx] = useState<{ deviceId: string; fromDate: Date } | undefined>();
    const [collectionToClearDeviceId, setCollectionToClearDeviceId] = useState<string | undefined>();
    // `coordsHelpers.getFacilityCoordsHelper` silently defaults to (0,0) when a
    // facility has no origin, so without this gate lane generation would draw on the
    // equator. The button is disabled when this is false.
    const facilityHasOrigin = !!facility.originReferencePointLatLng?.origin;

    const confirmDialog = useConfirmDialog();
    const showingDialog = useRef(false);

    class ParkingSpotModel {
        count: number = 1;
    }
    const parkingSpotDialog = useEditModelDialog(ParkingSpotModel);

    // cleanup map listeners on unmount
    useEffect(() => {
        return () => {
            mapManager.cleanup();
        };
    }, [mapManager]);

    useEffect(() => {
        const checkMapControls = () => {
            if (mapRef.current?.topLeftControlDiv && mapRef.current?.topRightControlDiv) {
                setMapControlsReady(true);
            }
        };

        checkMapControls();

        const interval = setInterval(() => {
            if (!mapControlsReady) {
                checkMapControls();
            } else {
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [mapRef.current?.topLeftControlDiv, mapRef.current?.topRightControlDiv, mapControlsReady]);

    useEffect(() => {
        const map = mapRef.current?.map;
        if (map) {
            toggleOverzoom(map, superZoomEnabled);
        }
    }, [superZoomEnabled, mapRef.current?.map]);

    // Render tracing for debugging
    useRenderingTrace("layout rerender", [facility, onChange, toolManager.editMode, toolManager.currentTool, coordsHelper, isSaving, shapeManager.selectedShapes.length, shapeLifecycle.copiedShapes.length]);

    // Filter available edit tools based on shape filter
    const availableEditTools = useMemo(() => {
        return EditTools.filter((tool) => {
            // Rectangle-drag parking spots are a map-only interaction; draw them as zones in camera view.
            if (viewMode === "camera" && tool.toolType === EditToolType.ParkingSpot) return false;
            switch (tool.toolType) {
                case EditToolType.DetectionZone:
                    return shapeFilter.showDetectionZonesInConfig;
                case EditToolType.ParkingSpot:
                    return shapeFilter.showDetectionZonesInConfig;
                case EditToolType.ScreenLine:
                    return shapeFilter.showScreenLinesInConfig;
                case EditToolType.LaneLine:
                    return shapeFilter.showLaneLinesInConfig;
                default:
                    return true;
            }
        });
    }, [shapeFilter, viewMode]);

    useImperativeHandle(ref, () => ({
        setEditMode: (mode: EditMode) => {
            toolManager.setEditMode(mode);
        },
        openCollectAndGenerate: () => {
            toolManager.setEditMode(EditMode.Layout);
            setIsCollectOpen(true);
        },
        openDeviceAlignment: (deviceId: string) => openDeviceAlignment(deviceId),
        centerMap: () => {
            mapManager.centerMap();
        },
        fitDevicesOnMap: () => {
            const map = mapRef.current?.map;
            if (!map || !coordsHelper) {
                return;
            }
            const deviceShapes = shapeManager.getLayoutConfiguration().devices ?? [];
            const bounds = new google.maps.LatLngBounds();
            deviceShapes.forEach((device) => {
                if (typeof device.positionX === "number" && typeof device.positionY === "number") {
                    bounds.extend(coordsHelper.toLatLng(new Vector2({ x: device.positionX, y: device.positionY })));
                }
            });

            if (!bounds.isEmpty()) {
                map.fitBounds(bounds, 60);
            } else {
                mapManager.centerMap();
            }
        },
        focusShapeById: (shapeId: string) => {
            const shape = shapeManager.getShapeById(shapeId) as any;
            if (!shape) {
                return false;
            }
            mapManager.panToShape(shape);
            return true;
        },
        setMapZoom: (zoom: number) => {
            mapRef.current?.map?.setZoom(zoom);
        },
        getLayoutConfiguration: () => shapeManager.getLayoutConfiguration(),
    }));

    // Event handlers
    const handleCancel: MouseEventHandler = useCallback(
        (e) => {
            toolManager.reset();
            shapeLifecycle.clearCopiedShapes();
            setAlignMode(false);
            setDraftOrigin(null);
            deviceAlignment.cancel();
        },
        [toolManager, shapeLifecycle, deviceAlignment.cancel], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleOriginDrag = useCallback((latLng: google.maps.LatLng) => {
        setDraftOrigin(new Vector2({ x: latLng.lng(), y: latLng.lat() }));
    }, []);

    const handleOriginDragEnd = useCallback(
        (latLng: google.maps.LatLng) => {
            const v = new Vector2({ x: latLng.lng(), y: latLng.lat() });
            setDraftOrigin(v);
            shapeManager.updateShape(makeOriginSpot(v));
        },
        [shapeManager, makeOriginSpot]
    );

    const enterAlignMode = useCallback(() => {
        const current = editedOriginSpot?.origin ?? facility.originReferencePointLatLng?.origin;
        if (!current) return;
        deviceAlignment.cancel();
        toolManager.clearTool();
        shapeManager.clearSelection();
        alignBaselineRef.current = new Vector2({ x: current.x, y: current.y });
        alignHadPriorEditRef.current = !!editedOriginSpot;
        setDraftOrigin(new Vector2({ x: current.x, y: current.y }));
        setAlignMode(true);
    }, [editedOriginSpot, facility.originReferencePointLatLng, toolManager, shapeManager, deviceAlignment.cancel]); // eslint-disable-line react-hooks/exhaustive-deps

    const revertAlignToBaseline = useCallback(() => {
        const baseline = alignBaselineRef.current;
        if (!baseline) return;
        if (alignHadPriorEditRef.current) {
            shapeManager.updateShape(makeOriginSpot(baseline));
        } else {
            const spot = shapeManager.localShapes.find((s) => s.$type === Spot.$type);
            if (spot?.id) shapeManager.removeLocalShape(spot.id);
        }
        setDraftOrigin(new Vector2({ x: baseline.x, y: baseline.y }));
    }, [shapeManager, makeOriginSpot]);

    const doneAlignMode = useCallback(() => {
        setAlignMode(false);
        setDraftOrigin(null);
    }, []);

    const abortAlignMode = useCallback(() => {
        revertAlignToBaseline();
        setAlignMode(false);
        setDraftOrigin(null);
    }, [revertAlignToBaseline]);

    const alignOffset = useMemo(() => {
        if (!alignMode || !draftOrigin) return null;
        return baseCoordsHelper.fromLatLng({ lat: draftOrigin.y, lng: draftOrigin.x });
    }, [alignMode, draftOrigin, baseCoordsHelper]);

    const alignOffsetLabel = useMemo(() => {
        if (!alignOffset) return "";
        const ew = `${Math.abs(alignOffset.x).toFixed(1)} m ${alignOffset.x >= 0 ? "E" : "W"}`;
        const ns = `${Math.abs(alignOffset.y).toFixed(1)} m ${alignOffset.y >= 0 ? "S" : "N"}`;
        return `Offset: ${ew} · ${ns}`;
    }, [alignOffset]);

    const alignHasMoved = !!alignOffset && (Math.abs(alignOffset.x) >= 0.05 || Math.abs(alignOffset.y) >= 0.05);

    const handleSave: MouseEventHandler = useCallback(
        async (e) => {
            setIsSaving(true);
            try {
                // Zones drawn to fewer corners than a polygon needs cannot be saved, and the save
                // says so below rather than reporting a clean success over the top of them.
                const zonesLeftOut = shapeManager.localShapes.filter(
                    (s) => s.$type === DetectionZone.$type && !(s as DetectionZone).canFormPolygon()
                );
                const config = shapeManager.getLayoutConfiguration();
                if (persistLayoutConfiguration) {
                    // The save is only a save if the server answered with the facility it wrote. A
                    // failure this call swallows rather than throws - a maintenance-mode 503, or an
                    // empty body - used to resolve as undefined and fall straight through to the
                    // success toast below, telling the user their layout was stored when it wasn't.
                    const saved = await Facilities.updateLayoutConfiguration(facility.id, config);
                    if (!saved) {
                        throw new Error("The server did not confirm the save.");
                    }
                }
                await onSaveLayoutConfiguration?.(config);
                if (collectionToClearDeviceId) {
                    try {
                        await Devices.cancelLaneCollection(collectionToClearDeviceId);
                        setCollectionToClearDeviceId(undefined);
                        toastSuccess("Collection ended — suggestions applied.");
                    } catch (err) {
                        console.error("Failed to end lane collection:", err);
                    }
                }
                onChange?.();
                toolManager.setEditMode(EditMode.None);
                if (zonesLeftOut.length > 0) {
                    const names = zonesLeftOut.map((s) => s.name || "Unnamed zone").join(", ");
                    toastWarning(
                        `Layout saved — ${zonesLeftOut.length === 1 ? "1 zone was" : `${zonesLeftOut.length} zones were`} left out`,
                        `${names}: a zone needs at least ${MINIMUM_ZONE_VERTICES} points. Draw the missing corners and save again.`,
                        8000
                    );
                } else {
                    toastSuccess("Layout saved successfully!");
                }
            } catch (error) {
                console.error("Failed to save layout:", error);
                // Stay in edit mode with the shapes still in hand: the layout is not stored, and the
                // user is the only one who can decide what to do about that.
                toastError("Layout not saved", error instanceof Error ? error.message : String(error));
            } finally {
                setIsSaving(false);
            }
        },
        [facility.id, shapeManager, persistLayoutConfiguration, onSaveLayoutConfiguration, onChange, toolManager, collectionToClearDeviceId]
    );

    const handleSplitSpot = useCallback(
        (shape: any, model: Partial<ParkingSpotModel>) => {
            shapeLifecycle.splitSpot(shape, model.count ?? 1);
        },
        [shapeLifecycle],
    );

    const showSplitDialog = useCallback(
        (shape: any) => {
            showingDialog.current = true;
            parkingSpotDialog.show(
                (model) => handleSplitSpot(shape, model),
                "Split into even spots",
                { count: 1 },
                () => (showingDialog.current = false),
            );
        },
        [parkingSpotDialog, handleSplitSpot],
    );

    const handleDescriptionClick = useCallback(
        (shape: any) => {
            if (!coordsHelper) return;
            mapManager.panToShape(shape);
        },
        [coordsHelper, mapManager],
    );

    // Keyboard shortcuts
    const inputFocusRef = useRef<boolean>(false);

    useEffect(() => {
        const handleFocus = (e: FocusEvent): void => {
            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable) {
                inputFocusRef.current = true;
            }
        };

        const handleBlur = (e: FocusEvent): void => {
            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable) {
                inputFocusRef.current = false;
            }
        };

        document.addEventListener("focusin", handleFocus);
        document.addEventListener("focusout", handleBlur);

        return () => {
            document.removeEventListener("focusin", handleFocus);
            document.removeEventListener("focusout", handleBlur);
        };
    }, []);

    useHeldKeyboardKeys(
        (keys) => {
            const isZPressed = !!Object.keys(keys.keysDown).find((x) => x === "KeyZ");
            if (showingDialog.current || toolManager.editMode !== EditMode.Layout) {
                return;
            }

            Object.keys(keys.keysDown).forEach((code) => {
                switch (code) {
                    case "KeyA":
                        if (isZPressed) {
                            const allSelectableShapes = shapeManager.allShapes.filter((s) => s.$type !== Device.$type && s.$type !== Spot.$type);
                            shapeManager.selectShapes(allSelectableShapes as any[]);
                        }
                        break;
                    case "KeyC":
                        if (isZPressed && viewMode === "map" && shapeManager.selectedShapes.length) {
                            shapeLifecycle.copyShapes();
                        }
                        break;
                    case "KeyV":
                        if (isZPressed && viewMode === "map" && shapeLifecycle.copiedShapes.length) {
                            shapeLifecycle.pasteShapes();
                        }
                        break;
                    case "KeyG":
                        if (isZPressed && !inputFocusRef.current) {
                            setGridSettings((prev) => ({ ...prev, enabled: !prev.enabled }));
                        }
                        break;
                    case "Delete":
                        if (inputFocusRef.current) return;
                        if (shapeManager.selectedShapes.length > 0) {
                            confirmDialog.show("Delete", `Are you sure you want to delete ${shapeManager.selectedShapes.length} items?`, "Delete", () => {
                                const toDelete = [...shapeManager.selectedShapes];
                                toDelete.forEach((s) => shapeLifecycle.handleShapeChanged(s, true));
                            });
                        }
                        break;
                }
            });
        },
        false,
        [shapeLifecycle, shapeManager, confirmDialog, toolManager.editMode, viewMode],
    );

    useEffect(() => {
        setAlignMode(false);
        setDraftOrigin(null);
        deviceAlignment.cancel();
    }, [facility.id, deviceAlignment.cancel]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!alignMode) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                abortAlignMode();
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [alignMode, abortAlignMode]);

    // Auto-scroll to selected shape
    useEffect(() => {
        const shape = shapeManager.selectedShapes[0];
        const selectedItem = document.getElementById("sc" + shape?.id);
        if (selectedItem) {
            selectedItem.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
        }
    }, [shapeManager.selectedShapes]);

    useEffect(() => {
        if (shapeManager.selectedShapes.length > 0 && window.innerWidth < 1280 && !toolManager.currentTool) {
            setIsMobileDrawerOpen(true);
        }
    }, [shapeManager.selectedShapes, toolManager.currentTool]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isMobileDrawerOpen) {
                setIsMobileDrawerOpen(false);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isMobileDrawerOpen]);

    const inProgressShape = useMemo<DetectionZone | LaneLine | ScreenLine | null>(() => {
        if (!toolManager.currentTool) return null;
        let mostRecent: DetectionZone | LaneLine | ScreenLine | null = null;
        let highestId = -1;
        shapeManager.allShapes.forEach((s) => {
            const hasCorrectType = s.$type === DetectionZone.$type || s.$type === LaneLine.$type || s.$type === ScreenLine.$type;
            const hasIsComplete = typeof (s as any).isComplete === "function";
            const isNotComplete = hasIsComplete && !(s as any).isComplete();
            const isTemporaryId = s.id && /^\d+$/.test(s.id);
            if (hasCorrectType && hasIsComplete && isNotComplete && isTemporaryId) {
                const numericId = parseInt(s.id || "0");
                if (!isNaN(numericId) && numericId > highestId) {
                    highestId = numericId;
                    mostRecent = s as DetectionZone | LaneLine | ScreenLine;
                }
            }
        });
        return mostRecent;
    }, [toolManager.currentTool, shapeManager.allShapes]);

    const toolHint = useMemo(() => {
        const toolType = toolManager.currentTool?.toolType;
        if (toolType === undefined) return null;
        const pointCount = (inProgressShape as any)?.points?.length ?? 0;
        // Middle-click completion is a map-view affordance; it doesn't reach the perspective canvas.
        const finishStep = viewMode === "camera" ? "Click the first point or Enter to finish" : "Click the first point, middle-click, or Enter to finish";
        switch (toolType) {
            case EditToolType.LaneLine:
                return { label: "Lane Line", pointCount, minPoints: 2, steps: ["Click to add points", "Double-click or Enter to finish", "Backspace to undo a point", "Esc to cancel"] };
            case EditToolType.ScreenLine:
                return { label: "Screen Line", pointCount, minPoints: 2, steps: ["Click to place two points", "Esc to cancel"] };
            // The number promised here is the number the save path honours: a zone with this many
            // corners is saved, closed for the user, whether or not they clicked back to the start.
            case EditToolType.ParkingSpot:
                return { label: "Parking Spot", pointCount, minPoints: MINIMUM_ZONE_VERTICES, steps: ["Click to add corners", finishStep, "Backspace to undo a point", "Esc to cancel"] };
            case EditToolType.DetectionZone:
                return { label: "Detection Zone", pointCount, minPoints: MINIMUM_ZONE_VERTICES, steps: ["Click to add points", finishStep, "Backspace to undo a point", "Esc to cancel"] };
            default:
                return null;
        }
    }, [toolManager.currentTool, inProgressShape, viewMode]);

    useEffect(() => {
        if (toolManager.editMode !== EditMode.Layout || !toolManager.currentTool) return;

        const handleDrawKey = (e: KeyboardEvent) => {
            if (inputFocusRef.current || showingDialog.current) return;

            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                toolManager.clearTool();
                return;
            }

            if (!inProgressShape) return;

            if (e.key === "Enter") {
                e.preventDefault();
                const points = (inProgressShape as any).points ?? [];
                if (inProgressShape.$type === LaneLine.$type && points.length >= 2) {
                    const completed = Object.assign(new LaneLine(), { ...inProgressShape, isExplicitlyCompleted: true });
                    shapeLifecycle.handleShapeChanged(completed as any, false, true);
                } else if (inProgressShape.$type === DetectionZone.$type && points.length >= MINIMUM_ZONE_VERTICES) {
                    // A zone closes by repeating its first vertex, which is what isComplete() checks.
                    const closed = Object.assign(Object.create(Object.getPrototypeOf(inProgressShape)), inProgressShape, { points: [...points, points[0]] });
                    shapeLifecycle.handleShapeChanged(closed as any, false, true);
                }
            } else if (e.key === "Backspace") {
                e.preventDefault();
                const points = [...((inProgressShape as any).points ?? [])];
                if (points.length > 0) {
                    points.pop();
                    const updated = Object.assign(Object.create(Object.getPrototypeOf(inProgressShape)), inProgressShape, { points });
                    shapeLifecycle.handleShapeChanged(updated, false);
                }
            }
        };

        document.addEventListener("keydown", handleDrawKey, true);
        return () => document.removeEventListener("keydown", handleDrawKey, true);
    }, [toolManager.editMode, toolManager.currentTool, toolManager, inProgressShape, shapeLifecycle]);

    // Info tooltip
    const infoTipId = useRef("");
    const infoClick = useCallback(() => {
        if (infoTipId.current) {
            dismissToast(infoTipId.current);
            infoTipId.current = "";
            return;
        }
        infoTipId.current = toastInfo(
            "Layout Editor Commands",
            <>
                <p>
                    <button className="kbc-button">Z</button> + <button className="kbc-button">C</button> = Copy Selected
                </p>
                <p>
                    <button className="kbc-button">Z</button> + <button className="kbc-button">V</button> = Paste Copied
                </p>
                <p>
                    <button className="kbc-button">Z</button> + <button className="kbc-button">A</button> = Select All
                </p>
                <p>
                    <button className="kbc-button">Z</button> + <button className="kbc-button">G</button> = Toggle Snap Grid
                </p>
                <p>
                    <button className="kbc-button">Double Click</button> / <button className="kbc-button">Enter</button> = Finish Lane Line
                </p>
                <p>
                    <button className="kbc-button">Backspace</button> = Undo Last Point
                </p>
                <p>
                    <button className="kbc-button">Esc</button> = Cancel Drawing
                </p>
                <p>
                    <button className="kbc-button">Middle Click</button> = Complete Mapping
                </p>
            </>,
            10000,
        );
    }, []);

    // Computed values
    const multipleSpotsSelected = useMemo(() => shapeManager.selectedShapes.length > 1 && shapeManager.selectedShapes.every((x) => x.$type === DetectionZone.$type), [shapeManager.selectedShapes]);

    const filteredMapShapes = useMemo(() => (multipleSpotsSelected ? shapeManager.allShapes.filter((es) => !shapeManager.selectedShapes.find((ss) => ss.id === es.id)) : shapeManager.allShapes), [shapeManager.allShapes, multipleSpotsSelected, shapeManager.selectedShapes]);

    const renderedMapShapes = useMemo(() => {
        let result = alignMode ? filteredMapShapes.filter((s) => s.$type !== Spot.$type) : filteredMapShapes;
        if (deviceAlignment.active) {
            // The rig (and the device being aligned) is rendered by DeviceAlignmentLayer at the candidate pose.
            result = result.filter((s) => !(s.id && deviceAlignment.rigShapeIds.has(s.id)) && s.id !== deviceAlignment.device?.id);
        }
        return result;
    }, [alignMode, filteredMapShapes, deviceAlignment.active, deviceAlignment.rigShapeIds, deviceAlignment.device?.id]);

    // Memoize selected shape IDs to prevent unnecessary re-renders
    const selectedShapeIds = useMemo(() => new Set(shapeManager.selectedShapes.map((s) => s.id)), [shapeManager.selectedShapes]);

    // Camera-perspective view: which device's camera we're looking through, preferring any locally edited pose
    const cameraDevice = useMemo(() => {
        const fromShapes = shapeManager.allShapes.find((s) => s.$type === Device.$type && s.id === cameraDeviceId) as Device | undefined;
        return fromShapes ?? facility.devices?.find((d) => d.id === cameraDeviceId) ?? facility.devices?.[0];
    }, [shapeManager.allShapes, facility.devices, cameraDeviceId]);

    const cameraShapes = useMemo(() => shapeManager.allShapes.filter((s) => s.$type === DetectionZone.$type || s.$type === ScreenLine.$type || s.$type === LaneLine.$type) as (DetectionZone | ScreenLine | LaneLine)[], [shapeManager.allShapes]);

    const switchViewMode = useCallback(
        (mode: "map" | "camera") => {
            if (mode === "camera") {
                doneAlignMode();
                deviceAlignment.cancel();
            }
            try {
                localStorage.setItem(viewModeStorageKey, mode);
            } catch {
                /* private mode / unavailable — fall back to in-session only */
            }
            setViewMode(mode);
        },
        [doneAlignMode, deviceAlignment.cancel, viewModeStorageKey], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const openDeviceAlignment = useCallback(
        (deviceId: string) => {
            setCameraDeviceId(deviceId);
            toolManager.setEditMode(EditMode.Layout);
            // Reference points are clicked against the map, so land there rather than on whichever
            // view the last session left in localStorage.
            switchViewMode("map");
            setReferenceAlignOpen(true);
        },
        [toolManager, switchViewMode], // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Provisioning hands a new device off to alignment by navigating here with its id. Wait for the
    // device to actually be in the facility we were given — the redirect can outrun the refetch —
    // and fire once, so closing the dialog doesn't immediately reopen it.
    const autoAlignFired = useRef(false);
    useEffect(() => {
        if (!autoAlignDeviceId || autoAlignFired.current) return;
        if (!facility.devices?.some((d) => d.id === autoAlignDeviceId)) return;
        autoAlignFired.current = true;
        openDeviceAlignment(autoAlignDeviceId);
        onAutoAlignOpened?.();
    }, [autoAlignDeviceId, facility.devices, openDeviceAlignment, onAutoAlignOpened]);

    const enterDeviceAlignMode = useCallback(() => {
        if (!cameraDevice) return;
        doneAlignMode();
        toolManager.clearTool();
        shapeManager.clearSelection();
        shapeLifecycle.clearCopiedShapes();
        setShowAlignTracks(true);
        deviceAlignment.enter(cameraDevice);
    }, [cameraDevice, doneAlignMode, toolManager, shapeManager, shapeLifecycle, deviceAlignment.enter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reference-point alignment finished: move the device (and its rig) to the fitted pose and
    // stamp the fitted camera fields on it. The layout's regular Save persists everything.
    const handleReferenceAlignmentApply = useCallback(
        (result: ReferenceAlignmentResult) => {
            if (!cameraDevice) return;
            deviceAlignment.applyPose(
                cameraDevice,
                { x: result.positionX, y: result.positionY, headingDegrees: result.headingDegrees },
                {
                    height: result.height,
                    fusionOffsetPitch: result.fusionOffsetPitch,
                    fusionOffsetRoll: result.fusionOffsetRoll,
                    desiredTiltDegrees: result.desiredTiltDegrees,
                },
            );
            setReferenceAlignOpen(false);
            toastSuccess("Device aligned — Save the layout to store it on the device.");
        },
        [cameraDevice, deviceAlignment.applyPose], // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Keyboard nudges while aligning a device: arrows slide the rig (Shift = bigger steps), Esc cancels.
    useEffect(() => {
        if (!deviceAlignment.active) return;
        const onKey = (e: KeyboardEvent) => {
            if (inputFocusRef.current || showingDialog.current) return;
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                deviceAlignment.cancel();
                return;
            }
            const step = e.shiftKey ? 1 : 0.25;
            switch (e.key) {
                case "ArrowLeft":
                    e.preventDefault();
                    e.stopPropagation();
                    deviceAlignment.nudge(-step, 0, 0);
                    break;
                case "ArrowRight":
                    e.preventDefault();
                    e.stopPropagation();
                    deviceAlignment.nudge(step, 0, 0);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    e.stopPropagation();
                    deviceAlignment.nudge(0, -step, 0);
                    break;
                case "ArrowDown":
                    e.preventDefault();
                    e.stopPropagation();
                    deviceAlignment.nudge(0, step, 0);
                    break;
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [deviceAlignment.active, deviceAlignment.cancel, deviceAlignment.nudge]); // eslint-disable-line react-hooks/exhaustive-deps

    const deviceAlignOffsetLabel = useMemo(() => {
        const o = deviceAlignment.offset;
        if (!o) return "";
        const ew = `${Math.abs(o.east).toFixed(1)} m ${o.east >= 0 ? "E" : "W"}`;
        const ns = `${Math.abs(o.south).toFixed(1)} m ${o.south >= 0 ? "S" : "N"}`;
        const rot = `${Math.abs(o.headingDelta).toFixed(1)}° ${o.headingDelta >= 0 ? "CW" : "CCW"}`;
        return `Δ ${ew} · ${ns} · ${rot}`;
    }, [deviceAlignment.offset]);

    // Memoize shape selection state to prevent MapShape re-renders
    const getShapeProps = useCallback(
        (shape: any) => ({
            isSelected: !multipleSpotsSelected && selectedShapeIds.has(shape.id),
            isSelectable: !shapeLifecycle.copiedShapes.length && !toolManager.currentTool && !mapManager.selectionBox && !deviceAlignment.active,
            isEditable: !shapeLifecycle.copiedShapes.length && selectedShapeIds.has(shape.id) && toolManager.editMode === EditMode.Layout && !deviceAlignment.active,
        }),
        [multipleSpotsSelected, selectedShapeIds, shapeLifecycle.copiedShapes.length, toolManager.currentTool, mapManager.selectionBox, toolManager.editMode, deviceAlignment.active],
    );

    const nameSortedShapes = useMemo(() => {
        // Combine local shapes with config shapes, but filter out deleted ones
        const localShapeIds = new Set(shapeManager.localShapes.map((s) => s.id));
        const configShapesToShow = shapeManager.configShapes.filter((s) => s.id && !shapeManager.deletedShapeIds.has(s.id) && !localShapeIds.has(s.id));

        return [...shapeManager.localShapes, ...configShapesToShow].sort(nameAndNumberSort);
    }, [shapeManager.localShapes, shapeManager.configShapes, shapeManager.deletedShapeIds]);

    const copyableShapes = [DetectionZone.$type, ScreenLine.$type, LaneLine.$type];

    const RenderHelpButton = () => {
        if (mapRef.current?.topRightControlDiv) {
            return ReactDOM.createPortal(
                <Button className="m-2 h-10 w-10 border-0 pl-2" onClick={infoClick}>
                    <Icons.Info size={24} />
                </Button>,
                mapRef.current.topRightControlDiv,
            );
        }
        return null;
    };

    const RenderCompass = () => {
        if (mapRef.current?.topLeftControlDiv) {
            return ReactDOM.createPortal(
                <div className="m-2">
                    <MapCompass />
                </div>,
                mapRef.current.topLeftControlDiv,
            );
        }
        return null;
    };

    const CenterMap = Icons.Target;
    const showLoading = useMemo(() => isSaving || !!isLoading || !!!facilityCenter, [isSaving, isLoading, facilityCenter]);

    if (showLoading) {
        return (
            <div className="flex">
                <div className="mx-auto mt-20">
                    <LoadingIndicator isLoading={showLoading} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row relative">
            <div className={cn("flex-1 xl:w-3/4 h-screen xl:h-auto relative", { "map-crosshair-cursor": !!toolManager.currentTool })}>
                {(facility.devices?.length ?? 0) > 0 && (
                    <div className="flex items-center gap-2 mb-2">
                        <div className="flex rounded border border-gray-300 overflow-hidden">
                            <button className={cn("px-3 py-1.5 text-sm font-medium flex items-center gap-1.5", viewMode === "map" ? "bg-brand-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50")} onClick={() => switchViewMode("map")} title="Top-down map view">
                                <Icons.MapMarker size={16} /> Map
                            </button>
                            <button className={cn("px-3 py-1.5 text-sm font-medium flex items-center gap-1.5", viewMode === "camera" ? "bg-brand-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50")} onClick={() => switchViewMode("camera")} title="Draw from the device camera's perspective">
                                <Icons.Video size={16} /> Camera
                            </button>
                        </div>
                        {(facility.devices?.length ?? 0) > 1 ? (
                            <select className="px-2 py-1.5 border rounded text-sm" value={cameraDeviceId ?? ""} onChange={(e) => setCameraDeviceId(e.target.value)} title="Active device" disabled={deviceAlignment.active}>
                                {facility.devices?.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name ?? d.id}
                                    </option>
                                ))}
                            </select>
                        ) : viewMode === "camera" ? (
                            <span className="text-sm text-gray-600">{cameraDevice?.name}</span>
                        ) : null}
                    </div>
                )}
                {viewMode === "camera" && (
                    <DeviceCameraView
                        device={cameraDevice}
                        shapes={cameraShapes}
                        inProgressShapeId={inProgressShape?.id}
                        editable={toolManager.editMode === EditMode.Layout && !shapeLifecycle.copiedShapes.length}
                        selectedShapeIds={selectedShapeIds as ReadonlySet<string>}
                        showLabels={{ detectionZones: showDetectionZoneLabels, screenLines: showScreenLineLabels, laneLines: showLaneLineLabels }}
                        onShapeChanged={shapeLifecycle.handleShapeChanged as any}
                        onShapeClick={(shapeId) => {
                            const shape = shapeManager.getShapeById(shapeId);
                            if (shape) shapeManager.selectShapes([shape as any]);
                        }}
                        onBackgroundClick={mapManager.handleMapClick}
                        snapshotOverride={cameraSnapshotOverride}
                    />
                )}
                {viewMode === "map" && (
                <OmniGoogleMap center={facilityCenter} height={mapHeight} margin={mapMargins} fitToPolygons={true} ref={mapRef} draggable={!mapManager.selectionBox && toolManager.currentTool?.toolType !== EditToolType.ParkingSpot} disableDoubleClickZoom={toolManager.editMode !== EditMode.None} onClick={mapManager.handleMapClick}>
                    {mapManager.selectionBox && <RectangleMapShape facility={liveFacility} shape={mapManager.selectionBox} isSelected={true} isEditable={true} onUpdate={mapManager.updateSelectionBox} color="blue" />}
                    {!!shapeLifecycle.copiedShapes?.length && <GroupMapShape facility={liveFacility} shapes={shapeLifecycle.copiedShapes} isSelected={true} isEditable={true} onClick={(positionedShapes) => shapeLifecycle.pasteShapes(positionedShapes)} color="blue" />}
                    {shapeLifecycle.copiedShapes.map((mp) => {
                        const isSelected = selectedShapeIds.has(mp.id);
                        const isEditable = shapeManager.selectedShapes.length === 1 && selectedShapeIds.has(mp.id) && toolManager.editMode === EditMode.Layout;
                        return <MapShape key={mp.id} shape={mp} facility={liveFacility} isSelected={isSelected} isEditable={isEditable} onUpdate={shapeLifecycle.handleShapeChanged as any} onClick={mapManager.handleShapeClick} showDetectionZoneLabels={showDetectionZoneLabels} showLaneLineLabels={showLaneLineLabels} showScreenLineLabels={showScreenLineLabels} />;
                    })}
                    {renderedMapShapes.map((mp) => {
                        const shapeProps = getShapeProps(mp);
                        return <MapShape key={mp.id} shape={mp} facility={liveFacility} isSelected={shapeProps.isSelected} isSelectable={shapeProps.isSelectable} isEditable={shapeProps.isEditable} onUpdate={shapeLifecycle.handleShapeChanged as any} onClick={mapManager.handleShapeClick} showDetectionZoneLabels={showDetectionZoneLabels} showLaneLineLabels={showLaneLineLabels} showScreenLineLabels={showScreenLineLabels} />;
                    })}
                    {multipleSpotsSelected && (
                        <ParkingSpotGroupMapShape
                            facility={liveFacility}
                            spots={shapeManager.selectedShapes as DetectionZone[]}
                            onUpdate={(shapes) => {
                                shapes.forEach((shape) => shapeManager.updateShape(shape));
                            }}
                        />
                    )}
                    {alignMode && draftOrigin && <AlignOriginHandle position={{ lat: draftOrigin.y, lng: draftOrigin.x }} onDrag={handleOriginDrag} onDragEnd={handleOriginDragEnd} />}
                    {deviceAlignment.active && <DeviceAlignmentLayer facility={liveFacility} alignment={deviceAlignment} showTracks={showAlignTracks} showDetectionZoneLabels={showDetectionZoneLabels} showLaneLineLabels={showLaneLineLabels} showScreenLineLabels={showScreenLineLabels} />}
                    {RenderHelpButton()}
                    {RenderCompass()}
                </OmniGoogleMap>
                )}

                {toolHint && (
                    <div className="pointer-events-none absolute top-3 left-1/2 z-40 -translate-x-1/2 w-max max-w-[95%]">
                        <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-gray-900/90 px-4 py-2 text-white shadow-lg">
                            <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold">
                                <Icons.Plus size={16} />
                                {toolHint.label}
                            </span>
                            <span className="whitespace-nowrap rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium">
                                {toolHint.pointCount} {toolHint.pointCount === 1 ? "point" : "points"}
                                {toolHint.pointCount < toolHint.minPoints ? ` · need ${toolHint.minPoints}+` : ""}
                            </span>
                            <span className="hidden items-center text-xs text-gray-200 sm:flex">
                                {toolHint.steps.map((step, idx) => (
                                    <span key={idx} className="whitespace-nowrap">
                                        {idx > 0 && <span className="mx-2 text-gray-500">·</span>}
                                        {step}
                                    </span>
                                ))}
                            </span>
                        </div>
                    </div>
                )}

                {viewMode === "map" && alignMode && draftOrigin && (
                    <div className="absolute top-3 left-1/2 z-40 -translate-x-1/2 w-max max-w-[95%]">
                        <div className="flex flex-col gap-1 rounded-lg bg-gray-900/90 px-4 py-2 text-white shadow-lg">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold">
                                    <Icons.MapMarker size={16} /> Aligning layout
                                </span>
                                <span className="hidden whitespace-nowrap text-xs text-gray-200 sm:inline">Drag the handle to slide everything onto the road.</span>
                                {alignHasMoved && <span className="whitespace-nowrap rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium">{alignOffsetLabel}</span>}
                                <span className="hidden whitespace-nowrap text-[11px] text-gray-400 md:inline">slides only — won't rotate</span>
                                <span className="flex gap-2">
                                    {alignHasMoved && (
                                        <button onClick={revertAlignToBaseline} className="rounded bg-white/15 px-3 py-1 text-xs hover:bg-white/25">
                                            Reset
                                        </button>
                                    )}
                                    <button onClick={doneAlignMode} className="rounded bg-white/15 px-3 py-1 text-xs font-semibold hover:bg-white/25">
                                        Done
                                    </button>
                                </span>
                            </div>
                            <span className="text-[11px] text-gray-300">Changes apply live — use Save below to keep them.</span>
                        </div>
                    </div>
                )}

                {viewMode === "map" && deviceAlignment.active && (
                    <div className="absolute top-3 left-1/2 z-40 -translate-x-1/2 w-max max-w-[95%]">
                        <div className="flex flex-col gap-1 rounded-lg bg-gray-900/90 px-4 py-2 text-white shadow-lg">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold">
                                    <Icons.Video size={16} /> Aligning {deviceAlignment.device?.name ?? "device"}
                                </span>
                                <span className="hidden whitespace-nowrap text-xs text-gray-200 lg:inline">Drag the blue dot to slide · orange handle rotates · arrow keys nudge</span>
                                {deviceAlignment.hasMoved && <span className="whitespace-nowrap rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium">{deviceAlignOffsetLabel}</span>}
                                {deviceAlignment.tracksLoading ? (
                                    <span className="flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-300">
                                        <span className="inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-white" /> loading traffic…
                                    </span>
                                ) : (
                                    <span className="whitespace-nowrap rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium" title="Recent target tracks from the last hour, shown in blue">
                                        {deviceAlignment.trackCount} tracks
                                    </span>
                                )}
                                <span className="flex gap-2">
                                    <button onClick={() => deviceAlignment.nudge(0, 0, -0.5)} className="rounded bg-white/15 px-2 py-1 text-xs hover:bg-white/25" title="Rotate 0.5° counter-clockwise">
                                        ⟲
                                    </button>
                                    <button onClick={() => deviceAlignment.nudge(0, 0, 0.5)} className="rounded bg-white/15 px-2 py-1 text-xs hover:bg-white/25" title="Rotate 0.5° clockwise">
                                        ⟳
                                    </button>
                                    <button onClick={() => setShowAlignTracks((current) => !current)} className="rounded bg-white/15 px-3 py-1 text-xs hover:bg-white/25" title={showAlignTracks ? "Hide the recent traffic overlay" : "Show the recent traffic overlay"}>
                                        {showAlignTracks ? "Hide tracks" : "Show tracks"}
                                    </button>
                                    <button onClick={deviceAlignment.refreshTracks} disabled={deviceAlignment.tracksLoading} className="rounded bg-white/15 px-3 py-1 text-xs hover:bg-white/25" title="Reload recent target tracks">
                                        Refresh
                                    </button>
                                    <button
                                        role="switch"
                                        aria-checked={deviceAlignment.moveGeometry}
                                        onClick={() => deviceAlignment.setMoveGeometry(!deviceAlignment.moveGeometry)}
                                        className="rounded bg-white/15 px-3 py-1 text-xs hover:bg-white/25"
                                        title={
                                            deviceAlignment.moveGeometry
                                                ? `This device's ${deviceAlignment.rigShapeCount} lane line(s), zone(s) and screen line(s) move with it — click to move only the device`
                                                : "Only the device moves; its lane lines, zones and screen lines stay where they are (recent tracks still follow the device) — click to move them too"
                                        }
                                    >
                                        {deviceAlignment.moveGeometry ? "☑" : "☐"} Move lanes &amp; zones
                                    </button>
                                    {deviceAlignment.hasMoved && (
                                        <button onClick={deviceAlignment.resetPose} className="rounded bg-white/15 px-3 py-1 text-xs hover:bg-white/25">
                                            Reset
                                        </button>
                                    )}
                                    <button onClick={deviceAlignment.commit} className="rounded bg-white/15 px-3 py-1 text-xs font-semibold hover:bg-white/25">
                                        Done
                                    </button>
                                    <button onClick={deviceAlignment.cancel} className="rounded bg-white/15 px-3 py-1 text-xs hover:bg-white/25">
                                        Cancel
                                    </button>
                                </span>
                            </div>
                            <span className="text-[11px] text-gray-300">
                                {deviceAlignment.rigShapeCount === 0 && deviceAlignment.trackCount === 0 && !deviceAlignment.tracksLoading
                                    ? "Nothing to align against yet — this device has no lane lines or recent traffic. Generate lane lines or wait for traffic first."
                                    : deviceAlignment.moveGeometry
                                      ? "Done applies the new device position/heading and moves its lane lines and zones with it — use Save below to keep everything."
                                      : "Done applies the new device position/heading only — its lane lines and zones stay where they are. Use Save below to keep it."}
                            </span>
                        </div>
                    </div>
                )}

                {viewMode === "map" && toolManager.editMode !== EditMode.None && <MapGrid map={mapRef.current?.map} gridSettings={gridSettings} referencePoint={facilityCenter} />}

                {viewMode === "map" && measurementsEnabled && (toolManager.currentTool || (toolManager.editMode === EditMode.Layout && shapeManager.selectedShapes.length > 0)) && (
                    <MapMeasurements
                        map={mapRef.current?.map}
                        enabled={measurementsEnabled}
                        shapes={toolManager.currentTool ? (inProgressShape ? [inProgressShape] : []) : (shapeManager.selectedShapes as (DetectionZone | LaneLine | ScreenLine)[])}
                        facility={liveFacility}
                        showDynamicMeasurement={!!toolManager.currentTool}
                    />
                )}

                <div className="xl:hidden fixed bottom-4 -translate-x-1/2 z-50 flex items-center gap-3" style={{ left: `calc(50% + ${NAV_SIDEBAR_WIDTH / 2}px)` }}>
                    {/* shapes button */}
                    {!showMapOnly && (
                        <button
                            onClick={() => {
                                setIsMobileControlsOpen(false);
                                setIsMobileDrawerOpen(!isMobileDrawerOpen);
                            }}
                            className="bg-brand-primary text-white px-5 py-3 rounded-full shadow-lg hover:bg-brand-primary-darkened transition-all flex items-center justify-center"
                            aria-label="Toggle shape list"
                        >
                            <div className="flex items-center justify-center gap-2">
                                {isMobileDrawerOpen ? <Icons.CaretDown size={20} /> : <Icons.CaretUp size={20} />}
                                <span className="text-sm font-medium whitespace-nowrap">Shapes - {nameSortedShapes?.length || 0}</span>
                            </div>
                        </button>
                    )}

                    {/* controls/edit button */}
                    {allowEdit && (
                        <button
                            onClick={() => {
                                if (toolManager.editMode === EditMode.None) {
                                    toolManager.setEditMode(EditMode.Layout);
                                } else {
                                    setIsMobileDrawerOpen(false);
                                    setIsMobileControlsOpen(!isMobileControlsOpen);
                                }
                            }}
                            className="bg-white text-brand-primary p-4 rounded-full shadow-lg hover:shadow-xl border-2 border-brand-primary transition-all flex items-center justify-center"
                            aria-label={toolManager.editMode === EditMode.None ? "Enter edit mode" : "Toggle map controls"}
                        >
                            {toolManager.editMode === EditMode.None ? <Icons.Edit size={24} /> : <Icons.Settings size={24} />}
                        </button>
                    )}
                </div>

                {isMobileControlsOpen && <div className="xl:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMobileControlsOpen(false)} />}

                {toolManager.editMode === EditMode.None && allowEdit && (
                    <div className="hidden xl:flex gap-1 mb-4 xl:mb-3">
                        {viewMode === "map" && mapRef.current?.reCenter && (
                            <Button onClick={mapManager.centerMap} className="ml-0">
                                <CenterMap size={24} />
                            </Button>
                        )}
                        <AuthorizeView claim={CustomClaimValue.EditSiteLayout} facilityId={facility.id} organizationId={facility.organizationId}>
                            <Button className="btn btn-outline-brand-orange mx-0" onClick={() => toolManager.setEditMode(EditMode.Layout)}>
                                <i className="fa fa-draw-polygon"></i>
                                Edit Layout
                            </Button>
                        </AuthorizeView>
                    </div>
                )}

                {toolManager.editMode !== EditMode.None && (
                    <div className={cn("flex gap-1 flex-wrap mb-4 xl:mb-0 transition-all duration-300", "xl:relative xl:translate-y-0", isMobileControlsOpen ? "fixed bottom-20 right-4 z-50 bg-white p-4 rounded-lg shadow-2xl max-h-[60vh] overflow-y-auto" : "xl:flex hidden")} style={isMobileControlsOpen ? { left: NAV_SIDEBAR_WIDTH + 4 } : undefined}>
                        {viewMode === "map" && (
                            <Button onClick={mapManager.centerMap} className="ml-0">
                                <CenterMap size={24} />
                            </Button>
                        )}
                        {viewMode === "map" && mapRef.current && (
                            <Button onClick={mapManager.startSelectionBox} className={cn("ml-0", { "border-brand-secondary border-4": !!mapManager.selectionBox })}>
                                <Icons.ObjectGroup size={24} />
                            </Button>
                        )}

                        {toolManager.editMode === EditMode.Layout && (
                            <>
                                <span className="mx-1 self-stretch border-l border-gray-300" aria-hidden />

                                {viewMode === "map" && (
                                    <Button
                                        onClick={() => (alignMode ? doneAlignMode() : enterAlignMode())}
                                        disabled={!facilityHasOrigin}
                                        className={cn("btn btn-outline-brand-orange mx-0 flex items-center gap-2 min-w-fit", { "border-brand-secondary border-4": alignMode })}
                                        title={facilityHasOrigin ? "If the lanes look off, slide the whole layout to line it up with the road — Save keeps it" : "Set a reference point on the facility first"}
                                    >
                                        <Icons.MapMarker size={20} />
                                        Align to Map
                                    </Button>
                                )}

                                {viewMode === "map" && (
                                    <Button
                                        onClick={() => (deviceAlignment.active ? deviceAlignment.commit() : setAlignChoiceOpen(true))}
                                        disabled={!facilityHasOrigin || !cameraDevice}
                                        className={cn("btn btn-outline-brand-orange mx-0 flex items-center gap-2 min-w-fit ml-0", { "border-brand-secondary border-4": deviceAlignment.active })}
                                        title={facilityHasOrigin ? "Line this device's lane lines and recent traffic up with the road — moves and rotates the device with its shapes, map-drawn shapes stay put" : "Set a reference point on the facility first"}
                                    >
                                        <Icons.Video size={20} />
                                        Align Device
                                    </Button>
                                )}

                                {shapeFilter.showLaneLinesInConfig && (
                                    <Button
                                        onClick={() => {
                                            doneAlignMode();
                                            setIsGenerateLaneLinesOpen(true);
                                        }}
                                        disabled={!facilityHasOrigin}
                                        className="flex items-center gap-2 min-w-fit ml-0"
                                        title={facilityHasOrigin ? "Generate lane lines automatically from recent vehicle traffic" : "Set a reference point on the facility before generating lane lines"}
                                    >
                                        Generate Lane Lines
                                    </Button>
                                )}

                                {shapeFilter.showLaneLinesInConfig && (facility.devices ?? []).some((d) => hasLaneCollection(d.firmwareVersion)) && (
                                    <Button
                                        onClick={() => {
                                            doneAlignMode();
                                            setIsCollectOpen(true);
                                        }}
                                        disabled={!facilityHasOrigin}
                                        className="flex items-center gap-2 min-w-fit ml-0"
                                        title={facilityHasOrigin ? "Collect fresh traffic, then generate lane lines from it" : "Set a reference point on the facility first"}
                                    >
                                        Collect & Generate
                                    </Button>
                                )}

                                {shapeFilter.showLaneLinesInConfig && shapeManager.laneLineSnapshot && (
                                    <Button
                                        onClick={() =>
                                            confirmDialog.show(
                                                "Reset to suggested lane lines",
                                                `This discards your manual changes to the lane lines and restores the ${shapeManager.laneLineSnapshot!.length} suggestion${shapeManager.laneLineSnapshot!.length === 1 ? "" : "s"} as they were generated. Detection zones and screen lines are not affected. This can't be undone.`,
                                                "Reset lane lines",
                                                () => shapeManager.restoreLaneLineSnapshot()
                                            )
                                        }
                                        disabled={!shapeManager.canResetLaneLines}
                                        className="flex items-center gap-2 min-w-fit ml-0"
                                        title={shapeManager.canResetLaneLines ? "Restore the suggested lane lines to how they were generated" : "No changes to the suggested lane lines yet."}
                                    >
                                        <Icons.Reset size={20} />
                                        Reset to Suggested
                                    </Button>
                                )}

                                <span className="mx-1 self-stretch border-l border-gray-300" aria-hidden />
                            </>
                        )}

                        {availableEditTools
                            .filter((t) => t.toolPurpose === EditToolPurpose.Layout && toolManager.editMode === EditMode.Layout)
                            .map((tool) => (
                                <Button
                                    key={tool.toolType}
                                    className={cn("flex items-center gap-3 min-w-fit ml-0", {
                                        "border-brand-secondary border-4": toolManager.currentTool?.toolType === tool.toolType,
                                    })}
                                    onClick={() => toolManager.selectTool(tool)}
                                >
                                    {toolManager.isToolLocked && toolManager.currentTool?.toolType === tool.toolType ? <Icons.Lock /> : <tool.icon />}
                                    {tool.name}
                                </Button>
                            ))}

                        {viewMode === "map" && (
                            <Button onClick={() => setGridSettings((prev) => ({ ...prev, enabled: !prev.enabled }))} className={cn("flex items-center gap-2 ml-0", { "border-brand-secondary border-4": gridSettings.enabled })} title="Toggle Snap Grid (Z+G)">
                                <Icons.Grid3x3 size={20} />
                                Grid
                            </Button>
                        )}

                        {viewMode === "map" && gridSettings.enabled && (
                            <select value={gridSettings.sizeMeters.toString()} onChange={(e) => setGridSettings((prev) => ({ ...prev, sizeMeters: parseFloat(e.target.value) }))} className="px-2 py-1 border rounded text-sm" title="Grid Size">
                                <option value="1">1m</option>
                                <option value="2">2m</option>
                                <option value="3">3m</option>
                                <option value="5">5m</option>
                            </select>
                        )}

                        {viewMode === "map" && (
                            <Button onClick={() => setMeasurementsEnabled((prev) => !prev)} className={cn("flex items-center gap-2 ml-0", { "border-brand-secondary border-4": measurementsEnabled })} title="Toggle Distance/Bearing Measurements">
                                <Icons.Ruler size={20} />
                                Measure
                            </Button>
                        )}

                        <span className="flex">
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button onClick={handleSave} className="mr-0" disabled={deviceAlignment.active} title={deviceAlignment.active ? "Finish aligning (Done or Cancel) before saving" : undefined}>
                                Save
                            </Button>
                        </span>
                    </div>
                )}

                {(shapeFilter.showDetectionZonesOnMap || shapeFilter.showLaneLinesOnMap || shapeFilter.showScreenLinesOnMap) && toolManager.editMode !== EditMode.None && (
                    <div className={cn("flex gap-3 flex-wrap mt-2 mb-4 xl:mb-0 text-sm", isMobileControlsOpen ? "xl:flex" : "xl:flex hidden")}>
                        {shapeFilter.showDetectionZonesOnMap && (
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={showDetectionZoneLabels} onChange={(e) => setShowDetectionZoneLabels(e.target.checked)} className="cursor-pointer" />
                                <span>Detection Zone Names</span>
                            </label>
                        )}
                        {shapeFilter.showLaneLinesOnMap && (
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={showLaneLineLabels} onChange={(e) => setShowLaneLineLabels(e.target.checked)} className="cursor-pointer" />
                                <span>Lane Line Names</span>
                            </label>
                        )}
                        {shapeFilter.showScreenLinesOnMap && (
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={showScreenLineLabels} onChange={(e) => setShowScreenLineLabels(e.target.checked)} className="cursor-pointer" />
                                <span>Screen Line Names</span>
                            </label>
                        )}
                    </div>
                )}
            </div>

            {!showMapOnly && (
                <>
                    {/* Desktop Side Panel */}
                    <MaxHeightContainer minHeight={300} className="hidden xl:block w-full lg:w-1/4">
                        {({ height }) => (
                            <div style={{ height: `${height}px` }} className="xl:overflow-y-auto">
                                <div className="shape-configurations">
                                    {nameSortedShapes?.map((shape) => (
                                        <ShapeConfiguration
                                            id={"sc" + shape.id}
                                            key={shape.id ?? shape.name}
                                            facility={facility}
                                            shape={shape}
                                            availableDevices={facility.devices ?? []}
                                            isSelected={!!shapeManager.selectedShapes.find((x) => x.id === shape.id)}
                                            isEditable={shapeManager.selectedShapes.length < 2 && toolManager.editMode === EditMode.Layout && !!shapeManager.selectedShapes.find((x) => x.id === shape.id)}
                                            onClick={handleDescriptionClick}
                                            onDelete={() => shapeLifecycle.handleShapeChanged(shape, true)}
                                            onSplit={() => showSplitDialog(shape)}
                                            onCopy={shape.$type && copyableShapes.includes(shape.$type) ? (s) => shapeLifecycle.copyShapes(s) : undefined}
                                            onChange={(newShape) => shapeLifecycle.handleShapeChanged(newShape as any, false)}
                                            toggleEdit={toolManager.editMode === EditMode.None ? () => toolManager.setEditMode(EditMode.Layout) : undefined}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </MaxHeightContainer>

                    <div className="xl:hidden">
                        {isMobileDrawerOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMobileDrawerOpen(false)} />}

                        <div className={cn("fixed bottom-0 right-0 bg-white z-40 shadow-2xl transition-transform duration-300 ease-in-out rounded-t-2xl", isMobileDrawerOpen ? "translate-y-0" : "translate-y-full")} style={{ maxHeight: "70vh", left: NAV_SIDEBAR_WIDTH }}>
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 rounded-t-2xl flex justify-between items-center">
                                <h3 className="font-semibold text-lg">Shapes ({nameSortedShapes?.length || 0})</h3>
                                <button onClick={() => setIsMobileDrawerOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close drawer">
                                    <Icons.Times size={20} />
                                </button>
                            </div>

                            <div className="overflow-y-auto p-4 pb-20" style={{ maxHeight: "calc(70vh - 64px)" }}>
                                <div className="shape-configurations space-y-2">
                                    {nameSortedShapes?.map((shape) => (
                                        <ShapeConfiguration
                                            id={"sc" + shape.id}
                                            key={shape.id ?? shape.name}
                                            facility={facility}
                                            shape={shape}
                                            availableDevices={facility.devices ?? []}
                                            isSelected={!!shapeManager.selectedShapes.find((x) => x.id === shape.id)}
                                            isEditable={shapeManager.selectedShapes.length < 2 && toolManager.editMode === EditMode.Layout && !!shapeManager.selectedShapes.find((x) => x.id === shape.id)}
                                            onClick={handleDescriptionClick}
                                            onDelete={() => shapeLifecycle.handleShapeChanged(shape, true)}
                                            onSplit={() => showSplitDialog(shape)}
                                            onCopy={shape.$type && copyableShapes.includes(shape.$type) ? (s) => shapeLifecycle.copyShapes(s) : undefined}
                                            onChange={(newShape) => shapeLifecycle.handleShapeChanged(newShape as any, false)}
                                            toggleEdit={toolManager.editMode === EditMode.None ? () => toolManager.setEditMode(EditMode.Layout) : undefined}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <GenerateLaneLinesDialog
                open={isGenerateLaneLinesOpen}
                facility={facility}
                defaultDeviceId={reviewCtx?.deviceId ?? defaultDeviceId}
                fromDate={reviewCtx?.fromDate}
                existingLaneLines={shapeManager.getLayoutConfiguration().laneLines ?? []}
                coordsHelper={coordsHelper}
                getNextTempId={toolManager.getNextTempId}
                onApplySuggestions={(generated, existing, replaceExisting) => {
                    if (reviewCtx?.deviceId) setCollectionToClearDeviceId(reviewCtx.deviceId);
                    return shapeManager.applySuggestedLaneLines(generated, existing, replaceExisting);
                }}
                hideScopeSelection={hideLaneGenerationScope}
                onClose={() => {
                    setIsGenerateLaneLinesOpen(false);
                    setReviewCtx(undefined);
                }}
            />
            <CollectAndGenerateDialog
                open={isCollectOpen}
                facility={facility}
                onClose={() => setIsCollectOpen(false)}
                onReview={(deviceId, fromDate) => {
                    setReviewCtx({ deviceId, fromDate });
                    setIsGenerateLaneLinesOpen(true);
                }}
                onChanged={() => onChange?.()}
            />
            <Dialog show={alignChoiceOpen} title="Align Device" showOkButton={false} showCancelButton={false} onCancelClick={() => setAlignChoiceOpen(false)} className="w-full max-w-xl">
                <p className="text-sm text-gray-700 mb-4">How do you want to align {cameraDevice?.name ?? "this device"}?</p>
                <div className="flex flex-col gap-1 mb-2">
                    <Button
                        className="w-full"
                        onClick={() => {
                            setAlignChoiceOpen(false);
                            setReferenceAlignOpen(true);
                        }}
                    >
                        Reference Points
                    </Button>
                    <p className="text-sm text-gray-600 mb-3">Click matching points on the camera and the map — fits the device position, heading, height, pitch, and roll from them. Best for setting up a device.</p>
                    <Button
                        className="w-full"
                        onClick={() => {
                            setAlignChoiceOpen(false);
                            enterDeviceAlignMode();
                        }}
                    >
                        Manual
                    </Button>
                    <p className="text-sm text-gray-600">Drag and rotate the device — with its recent traffic — over the map imagery. Its lane lines and zones move with it by default, or can be held in place to move just the device.</p>
                </div>
            </Dialog>
            <DeviceReferenceAlignmentDialog open={referenceAlignOpen} device={cameraDevice} facility={facility} onCancel={() => setReferenceAlignOpen(false)} onApply={handleReferenceAlignmentApply} snapshotOverride={cameraSnapshotOverride} />
            {confirmDialog.renderDialog()}
            {parkingSpotDialog.renderDialog((spot, helpers) => (
                <>
                    <InputRow>
                        <NumericInput label="Number of Spots" value={spot.count} onChange={(c) => helpers.replaceModel({ count: c ?? spot.count ?? 1 })} />
                    </InputRow>
                </>
            ))}
        </div>
    );
});

// Helper function for sorting shapes by name and number
function nameAndNumberSort(a: any, b: any): number {
    const aTypeName = shapeToName(a);
    const aName = a.name?.split(aTypeName);

    const bTypeName = shapeToName(b);
    const bName = b.name?.split(bTypeName);

    for (let idx = 0; idx < (aName?.length ?? 0); idx++) {
        if ((bName?.length ?? -1) <= idx) {
            break;
        }
        const part = aName![idx];
        const bPart = bName![idx];
        const val = parseInt(part);
        if (!isNaN(val)) {
            const bVal = parseInt(bPart);
            if (!isNaN(bVal)) {
                return val - bVal;
            }
        }
        if (part !== bPart) {
            return part.localeCompare(bPart);
        }
    }
    return a.name?.localeCompare(b.name ?? "") ?? 0;
}
